import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Divider, Icon, IconButton, ListItemIcon, Menu, MenuItem, Snackbar, Tooltip } from '@mui/material';
import { useIsAuthenticated } from '@azure/msal-react';

import ProgressBar from '../../controls/ProgressBar';
import NoActiveOrganisatieMessage from '../../controls/NoActiveOrganisatieMessage';
import DialogPortfolioStart from '../../controls/dialogs/DialogPortfolioStart';
import DialogPortfolioAddPlans from '../../controls/dialogs/DialogPortfolioAddPlans';
import DialogPortfolioConsortiums from '../../controls/dialogs/DialogPortfolioConsortiums';
import OverzichtPlannen from './OverzichtPlannen';
import OverzichtAnnotations from './OverzichtAnnotations';
import AzureMap from '../../controls/AzureMaps';


import { MijnOrganisatieListItem } from '../../../models/user/mijnOrganisatieListItem';
import { Portfolio } from '../../../models/portfolio/portfolio';

import { userManagementService } from '../../../services/user-management.service';
import { eventBus } from '../../../services/event-emitter.service';
import { mapService } from '../../../services/map.service';
import { portfolioService } from '../../../services/portfolio.service';
import { authService } from '../../../services/auth.service';

import TaskSwimlanes from './TaskSwimlanes';
import ErrorMessage from '../../controls/ErrorMessage';
import { navigateTo } from '../../../services/utils/navigation';


interface PortfolioDetailProps {
  portfolioId?: string; // ID of the current portfolio
  zoomInOnPlannenBbox?: (bbox: any) => void;
  refetchPortfolios?: () => void;
  setPortfolioNaam?: (title: string) => void;
  setPortfolioOmschrijving?: (title: string) => void;
}

const PortfolioDetail: React.FC<PortfolioDetailProps> = ({
  portfolioId: pId,
  zoomInOnPlannenBbox,
  refetchPortfolios,
  setPortfolioNaam,
  setPortfolioOmschrijving
}) => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();

  const { id: urlPortfolioId } = useParams<{ id: string }>(); // Get ID from URL

  // State to store the list of plans
  const [portfolio, setPortfolio] = useState<Portfolio>({} as Portfolio);
  const [mijnOrganisatie, setMijnOrganisatie] = useState<MijnOrganisatieListItem | null>(null);
  const [organizations, setOrganizations] = useState<any[] | null>(null);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    snackbar: false,
    plan: false,
    portfolio: false,
    consortiums: false,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menu, setOpenMenu] = useState({
    plan: false,
    portfolio: false,
    consortium: false,
    annotation: false,
    task: false,
  });

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open, menuToClose?: keyof typeof menu) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
    if (menuToClose) {
      closeMenu(menuToClose);
    }
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, name: keyof typeof menu) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(prev => ({ ...prev, [name]: true }));
  };

  const closeMenu = (name: keyof typeof menu) => {
    setAnchorEl(null);
    setOpenMenu(prev => ({ ...prev, [name]: false }));
  };

  const openDialogByEmit = (dialog: string, menuToClose?: keyof typeof menu) => {
    eventBus.emit('openDialog', dialog);

    if (menuToClose) {
      closeMenu(menuToClose);
    }
  };

  // Ref to prevent fetching data twice in Strict Mode
  const hasFetchedData = useRef(false);

  const portfolioId = pId || urlPortfolioId; // Use prop ID if available, otherwise URL ID
  const canCrud = !isReadOnly;

  useEffect(() => {
    setIsReadOnly(authService.isReadOnly());
  }, []);

  const fetchPortfolio = useCallback(async () => {
    try {
      if (portfolioId) {
        setLoading(true);
        const data = await portfolioService.getPortfolioIncludingCalculatedBbox(portfolioId); // getPortfolioInclPlanData

        // console.log('Fetched portfolio data:', data);
        if (data) {
          setPortfolio(data);

          if (setPortfolioNaam && data.name) {
            setPortfolioNaam(data.name);
          }

          if (setPortfolioOmschrijving && data.description) {
            setPortfolioOmschrijving(data.description);
          }

          getOrganizations(data);

          if (mapService.isValidBbox(data.bbox)) {
            eventBus.emit('zoomInOnPlanBbox', data.bbox);
          }
        }

        setLoading(false);
      }
    }
    catch (error: any) {
      const rawResponse = error?.response?.data;

      const errorMessage =
        typeof rawResponse === 'string'
          ? rawResponse
          : error?.message || 'Er is een fout opgetreden';

      if (error.status === 401 || error.status === 403) {
        // Geen toegang
        if (setPortfolioNaam) {
          setPortfolioNaam('Geen toegang');
        }
        if (setPortfolioOmschrijving) {
          setPortfolioOmschrijving('Als je wil deelnemen aan dit afstemmingsdossier, neem dan contact op met een organisatie die aan dit dossier deelneemt.');
        }
        setLoading(false);
        return;
      }

      if (error.status === 500 && errorMessage.includes('is not part of')) {
        navigateTo("/niet-toegestaan");

        setLoading(false);
        return;
      }

      // Algemene fout
      setError(errorMessage || 'Er is een fout opgetreden');
      setLoading(false);
    }
  }, [portfolioId, setPortfolioNaam, setPortfolioOmschrijving]);

  // Fetch the plans data from API when the component mounts
  useEffect(() => {
    if (!portfolioId) return;

    const storedOrganisatie = userManagementService.getStoredOrganisatie();
    if (storedOrganisatie) {
      setMijnOrganisatie(storedOrganisatie);
    }

    if (!hasFetchedData.current) {
      fetchPortfolio();
      hasFetchedData.current = true; // Mark as fetched
    }
  }, [portfolioId, fetchPortfolio]); // Run whenever infplId changes // [] means only once

  useEffect(() => {
    if (mapService.isValidBbox(portfolio?.bbox)) {
      // Wait a tick to ensure AzureMap is mounted
      const timeout = setTimeout(() => {
        eventBus.emit('zoomInOnPlanBbox', portfolio.bbox);
      }, 50); // or 50-100ms if needed

      return () => clearTimeout(timeout);
    }
  }, [portfolio]);

  const triggerReloadPortfolio = useCallback(async (_status: string) => {
    await fetchPortfolio();
  }, [fetchPortfolio]);

  const getOrganizations = async (data: any) => {
    // const acceptedPlans = data.plans.filter((plan: { inclusionRequestStatus: string }) => plan.inclusionRequestStatus === 'accepted');

    const uniqueOrganizations = Object.values(
      data.plans.reduce(
        (
          acc: Record<string, { id: string; name: string; active: boolean }>,
          plan: { organizationId: string; organizationName: string; inclusionRequestStatus: string }
        ) => {
          const id = plan.organizationId;
          if (!acc[id]) {
            acc[id] = {
              id: plan.organizationId,
              name: plan.organizationName,
              active: (plan.inclusionRequestStatus === 'accepted'),
            };
          }
          return acc;
        },
        {}
      )
    );

    setOrganizations(uniqueOrganizations);
  };

  const onSubmitPlan = useCallback(async (infplId: string) => {
    try {
      if (portfolioId && infplId) {
        setLoading(true);

        const response = await portfolioService.addPlanDeelnameToPortfolio(infplId, portfolioId);
        if (!response) {
          setLoading(false);
          return;
        }

        if (response) {
          setTimeout(async () => {
            setSnackbarMessage('Plan toegevoegd aan afstemmingsdossier');
            setOpenDialogs((prev) => ({ ...prev, snackbar: true }));

            await fetchPortfolio();
            setLoading(false);
          }, 3000);
        }
      }
    }
    catch (error: any) {
      setError(error.message || 'Er is een fout opgetreden');
      // setError('Er is een fout opgetreden');
      setLoading(false);
    }
  }, [portfolioId, fetchPortfolio]);

  const addPlanToPortfolio = useCallback(async (infplId: string, currrentPortfolioId: string) => {
    if (portfolioId !== currrentPortfolioId) return;

    await onSubmitPlan(infplId);
  }, [portfolioId, onSubmitPlan]);

  useEffect(() => {
    eventBus.on('reloadPortfolio', triggerReloadPortfolio);
    eventBus.on('addPlanToPortfolio', addPlanToPortfolio);

    // Cleanup
    return () => {
      eventBus.off('reloadPortfolio', triggerReloadPortfolio);
      eventBus.off('addPlanToPortfolio', addPlanToPortfolio);
    };
  }, [addPlanToPortfolio, triggerReloadPortfolio]);

  const currentOrganizationHasAcceptedPlans = () => {
    if (!portfolio || !mijnOrganisatie) return false;

    const activePlans = portfolio.plans?.filter(plan =>
      plan.organizationId === mijnOrganisatie.id && plan.inclusionRequestStatus === 'accepted'
    );

    return activePlans && activePlans.length > 0;
  };

  const handleSubmitPortfolio = (portfolio: Portfolio) => {
    handlePortfolio(portfolio);
    closeDialog('portfolio');
  };

  const handlePortfolio = async (portfolio: Portfolio) => {
    if (!portfolio) {
      console.error('Portfolio is null');
      return;
    }

    try {
      setLoading(true);
      const response = await portfolioService.updatePortfolio(portfolio);

      if (!response) {
        setLoading(false);
        // Handle error based on HTTP status code
        const errorMessage = await response.text();
        console.error('Failed to update portfolio:', errorMessage);
        throw new Error(errorMessage);
      }

      setTimeout(() => {
        setLoading(false);
        setSnackbarMessage('Afstemmingsdossier bijgewerkt');
        openDialog('snackbar');

        fetchPortfolio();
      }, 4000);

    }
    catch (error) {
      setLoading(false);
      console.error('Error updating portfolio:', error);
      setError('Er is een fout opgetreden');
    }
  }

  if (!mijnOrganisatie) {
    return (
      <>
        <NoActiveOrganisatieMessage />
      </>
    )
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key={'topcenter'}
      />

      {isAuthenticated && portfolio && mijnOrganisatie && (
        <div className="text-left">
          <div className="navigation nav-functions nav-functions-clear nav-functions-left">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => navigate('/dossiers')}>
              <Tooltip title="Terug naar afstemmingsdossiers" arrow>
                <Icon>arrow_backward</Icon>
              </Tooltip>
            </IconButton>
          </div>

          {portfolio.activeParticipant && canCrud && (
            <div className="navigation nav-functions nav-functions-clear nav-functions-right">

              <IconButton
                color="secondary"
                size="small"
                onClick={(event) => openMenu(event, 'portfolio')}
              >
                <Tooltip title="Bewerk het afstemmingsdossier of voeg plannen toe" arrow>
                  <Icon>work</Icon>
                </Tooltip>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menu.portfolio}
                onClose={() => closeMenu('portfolio')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => {
                  openDialog('plan');
                  closeMenu('portfolio');
                }}>
                  <ListItemIcon>
                    <Icon>add</Icon>
                  </ListItemIcon>
                  Plan toevoegen
                </MenuItem>
                <MenuItem onClick={() => {
                  openDialog('portfolio');
                  closeMenu('portfolio');
                }}>
                  <ListItemIcon>
                    <Icon>edit</Icon>
                  </ListItemIcon>
                  Wijzigen
                </MenuItem>
              </Menu>

              {currentOrganizationHasAcceptedPlans() && (
                <>
                  <IconButton
                    color="secondary"
                    size="small"
                    onClick={(event) => openMenu(event, 'annotation')}
                  >
                    <Tooltip title="Voeg notities toe" arrow>
                      <Icon>chat</Icon>
                    </Tooltip>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={menu.annotation}
                    onClose={() => closeMenu('annotation')}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem onClick={() => {
                      openDialogByEmit('annotation', 'annotation');
                    }}>
                      <ListItemIcon>
                        <Icon>add</Icon>
                      </ListItemIcon>
                      Notitie toevoegen
                    </MenuItem>
                  </Menu>

                  <IconButton
                    color="secondary"
                    size="small"
                    onClick={(event) => openMenu(event, 'task')}
                  >
                    <Tooltip title="Voeg acties toe" arrow>
                      <Icon>assignment</Icon>
                    </Tooltip>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={menu.task}
                    onClose={() => closeMenu('task')}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem onClick={() => {
                      openDialogByEmit('task', 'task');
                    }}>
                      <ListItemIcon>
                        <Icon>add</Icon>
                      </ListItemIcon>
                      Actie toevoegen
                    </MenuItem>
                  </Menu>
                </>
              )}

              <IconButton
                color="secondary"
                size="small"
                onClick={(event) => openMenu(event, 'consortium')}
              >
                <Tooltip title={isReadOnly ? 'Bekijk samenwerkingsverbanden' : 'Samenwerkingsverbanden beheren'} arrow>
                  <Icon>groups</Icon>
                </Tooltip>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menu.consortium}
                onClose={() => closeMenu('consortium')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => {
                  openDialog('consortiums');
                  closeMenu('consortium');
                }}>
                  <ListItemIcon>
                    <Icon>{isReadOnly ? 'list' : 'edit'}</Icon>
                  </ListItemIcon>
                  {isReadOnly ? 'Bekijk samenwerkingsverbanden' : 'Samenwerkingsverbanden beheren'}
                </MenuItem>
              </Menu>

            </div>
          )}

          {portfolio.activeParticipant && !canCrud && (
            <div className="navigation nav-functions nav-functions-clear nav-functions-right">
              <IconButton
                color="secondary"
                size="small"
                onClick={(event) => openMenu(event, 'consortium')}
              >
                <Tooltip title={isReadOnly ? 'Bekijk samenwerkingsverbanden' : 'Samenwerkingsverbanden beheren'} arrow>
                  <Icon>groups</Icon>
                </Tooltip>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menu.consortium}
                onClose={() => closeMenu('consortium')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => {
                  openDialog('consortiums');
                  closeMenu('consortium');
                }}>
                  <ListItemIcon>
                    <Icon>{isReadOnly ? 'list' : 'edit'}</Icon>
                  </ListItemIcon>
                  {isReadOnly ? 'Bekijk samenwerkingsverbanden' : 'Samenwerkingsverbanden beheren'}
                </MenuItem>
              </Menu>
            </div>
          )}

          {/* <pre>
            <code>{JSON.stringify(plan, null, 2)}</code>
          </pre> */}

          <div className="container container-fluid">
            <div className="row">
              <div className="col-12 col-sm-12">
                <div className="map-placeholder">
                  { mapService.isValidBbox(portfolio?.bbox) && (
                    <AzureMap portfolioId={portfolio?.id ?? undefined} portfolioActiveParticipant={portfolio.activeParticipant} />
                  )}
                  <span>
                    Kaartweergave
                  </span>
                </div>
              </div>
              <div className="col-12 col-sm-12">
                <div className="row">
                  <div className="col-12 col-sm-12 col-md-12 col-lg-12">
                    <div className="component-header">
                      <h4>
                        <Icon>map</Icon> Plannen
                      </h4>
                      {portfolio.activeParticipant && canCrud && (
                        <div className="btn-group">
                          <Tooltip title="Nieuw plan toevoegen aan dit dossier" placement="right" arrow>
                            <Button variant="text" color="secondary" onClick={ () => openDialog('plan') }>
                              <Icon>add</Icon>
                              Plan
                            </Button>
                          </Tooltip>
                        </div>
                      )}
                    </div>

                    { portfolio?.plans && (
                      <OverzichtPlannen
                        plannen={portfolio?.plans}
                        activeParticipant={portfolio.activeParticipant}
                        mijnOrganisatieId={mijnOrganisatie.id} />
                    )}
                  </div>

                  {/* <div className="col-12 col-sm-12 col-md-12 col-lg-4">
                    <OverzichtPortfolioConsortiums
                      consortiums={portfolio?.consortiums}
                      canManage={portfolio.activeParticipant}
                      onAddClick={() => openDialog('consortiums')}
                    />
                  </div> */}
                </div>

                <Divider className="divider divider-10 divider-transparent" />
              </div>
            </div>
          </div>

          <div className="container container-fluid">
            <div className="row">
              {portfolio?.id && mijnOrganisatie?.id && (
                <>
                  {!currentOrganizationHasAcceptedPlans() && (
                    <div className="col-12 col-sm-12">
                      <div className="melding">
                        <Icon>info</Icon>
                        <span>
                          Uw organisatie neemt nog niet actief deel aan dit afstemmingsdossier. Daardoor zijn acties en notities nog niet beschikbaar.
                        </span>
                      </div>
                    </div>
                  )}

                  {currentOrganizationHasAcceptedPlans() && (
                    <>
                      <div className="col-12 col-sm-12 col-md-12 col-lg-6">
                        <OverzichtAnnotations
                          portfolioId={portfolio.id}
                          mijnOrganisatieId={mijnOrganisatie.id}
                          organizations={organizations || []}
                          readOnly={isReadOnly}
                        />
                      </div>
                      <div className="col-12 col-sm-12 col-md-12 col-lg-6">
                        {organizations && (
                          <TaskSwimlanes
                            portfolioId={portfolio.id}
                            mijnOrganisatieId={mijnOrganisatie.id}
                            organizations={organizations}
                            readOnly={isReadOnly}
                          />
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      { portfolio && (
        <>
          <DialogPortfolioStart
            open={open.portfolio}
            portfolioId={portfolio.id || undefined}
            onClose={ () => closeDialog('portfolio') }
            onSubmit={handleSubmitPortfolio} />

          <DialogPortfolioAddPlans
            open={open.plan}
            portfolio={portfolio}
            onClose={ () => closeDialog('plan') }
            onSubmit={onSubmitPlan} />

          <DialogPortfolioConsortiums
            open={open.consortiums}
            portfolioId={portfolio.id || undefined}
            onClose={() => closeDialog('consortiums')}
            onUpdated={fetchPortfolio}
          />
        </>
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

      { loading && (
        <ProgressBar invert={false} />
      )}
    </>
  );
};

export default PortfolioDetail;